import { useEffect, useState } from 'react'

// Global flag to ensure version check only runs once
let versionCheckInitialized = false
let lastUpdateCheckTime = 0

const VERSION_PATHS = ['/', '/public/']

const fetchLatestVersion = async () => {
    const cacheBuster = `?t=${Date.now()}`

    for (const dir of VERSION_PATHS) {
        try {
            const response = await fetch(`${dir.replace(/\/$/, '')}/version.json${cacheBuster}`)
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            return await response.json()
        } catch (error) {
            console.log(`>>> Couldn't find or parse version.json in ${dir}, trying next location`)
        }
    }

    throw new Error('Failed to fetch version from all locations')
}

const updateCurrentVersion = version => {
    if (!window.Knov) return

    window.Knov.version = version
    window.dispatchEvent(
        new CustomEvent('versionDataUpdate', {
            detail: { version },
        }),
    )
}

const updateVersionStatus = (isOutdated: boolean) => {
    if (window.Knov) {
        window.Knov.isUpdateAvailable = isOutdated
    }

    lastUpdateCheckTime = Date.now()

    window.dispatchEvent(
        new CustomEvent('versionCheckUpdate', {
            detail: { isUpdateAvailable: isOutdated, timestamp: lastUpdateCheckTime },
        }),
    )
}

/**
 * Hook that periodically checks for application updates by comparing the
 * current version with the latest version from the server.
 *
 * @param {number} intervalMinutes - How often to check for updates (in minutes)
 * @returns {{ isUpdateAvailable: boolean }} Whether an update is available
 */
export function useVersionCheck(intervalMinutes = 30) {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

    useEffect(() => {
        // If window.Knov.isUpdateAvailable is already set, use that value immediately
        // This handles the case where the version check completed before this component mounted
        if (window.Knov && typeof window.Knov.isUpdateAvailable === 'boolean') {
            setIsUpdateAvailable(window.Knov.isUpdateAvailable)
        }

        // Only run the version check initialization once per app session
        if (versionCheckInitialized) {
            return
        }

        // Mark as initialized to prevent duplicate initialization
        versionCheckInitialized = true

        const getCurrentVersion = () => {
            if (!window.Knov?.version) return null
            return {
                buildTimeUnixTimestamp: window.Knov.version.buildTimeUnixTimestamp,
                gitCommitHash: window.Knov.version.gitCommitHash,
            }
        }

        // Function to check for updates
        const checkForUpdates = async () => {
            try {
                const currentVersion = getCurrentVersion()
                if (!currentVersion) {
                    const hydratedVersion = await hydrateCurrentVersion()
                    if (hydratedVersion) {
                        return { isUpdateAvailable: false, currentVersion: hydratedVersion }
                    }
                    console.warn('Version check skipped: current version metadata is not available')
                    updateVersionStatus(false)
                    return { isUpdateAvailable: false }
                }

                const remoteVersion = await fetchLatestVersion()

                // Compare versions - consider app outdated if either timestamp or git hash has changed
                const isOutdated =
                    remoteVersion.buildTimeUnixTimestamp > currentVersion.buildTimeUnixTimestamp ||
                    remoteVersion.gitCommitHash !== currentVersion.gitCommitHash

                // Update all states and emit event
                updateVersionStatus(isOutdated)
                setIsUpdateAvailable(isOutdated)
                //updateVersionStatus(true) // for testing.

                if (isOutdated) {
                    console.log(
                        `Update available:\n`,
                        `Current version: ${currentVersion.gitCommitHash} (${new Date(
                            currentVersion.buildTimeUnixTimestamp,
                        ).toISOString()})\n`,
                        `Latest version: ${remoteVersion.gitCommitHash} (${new Date(
                            remoteVersion.buildTimeUnixTimestamp,
                        ).toISOString()})`,
                    )
                }

                return { isUpdateAvailable: isOutdated, currentVersion, remoteVersion }
            } catch (error) {
                console.error('Error checking for updates:', error)
                return { error }
            }
        }

        const hydrateCurrentVersion = async () => {
            try {
                const currentVersion = getCurrentVersion()
                if (currentVersion) {
                    updateCurrentVersion(currentVersion)
                    return currentVersion
                }

                console.warn('Version metadata is still unavailable after hydration attempt')
                return null
            } catch (error) {
                console.error('Error hydrating current version:', error)
                return null
            }
        }

        // Set up an event listener for version updates
        const handleVersionUpdate = event => {
            if (event.detail && typeof event.detail.isUpdateAvailable === 'boolean') {
                setIsUpdateAvailable(event.detail.isUpdateAvailable)
            }
        }
        window.addEventListener('versionCheckUpdate', handleVersionUpdate)

        // Expose the function on window.Knov
        if (window.Knov) {
            window.Knov.checkVersionUpdate = checkForUpdates
            // Initialize the isUpdateAvailable property if it doesn't exist yet
            if (typeof window.Knov.isUpdateAvailable === 'undefined') {
                window.Knov.isUpdateAvailable = false
            }
        }

        // Initial check after a brief delay (2 seconds)
        const initialCheckTimeout = setTimeout(() => {
            checkForUpdates()

            // After a slight additional delay, resend the update status event
            // This helps catch any components that may have missed the first event due to race conditions
            setTimeout(() => {
                if (window.Knov && typeof window.Knov.isUpdateAvailable === 'boolean') {
                    window.dispatchEvent(
                        new CustomEvent('versionCheckUpdate', {
                            detail: {
                                isUpdateAvailable: window.Knov.isUpdateAvailable,
                                timestamp: lastUpdateCheckTime,
                                isRebroadcast: true,
                            },
                        }),
                    )
                }
            }, 500)
        }, 2000)

        // Set up periodic checks
        const intervalId = setInterval(checkForUpdates, intervalMinutes * 60 * 1000)

        // Clean up on component unmount
        return () => {
            clearTimeout(initialCheckTimeout)
            clearInterval(intervalId)
            window.removeEventListener('versionCheckUpdate', handleVersionUpdate)
            // Do NOT reset versionCheckInitialized here, as we want it to stay initialized
            // for the lifetime of the application
        }
    }, []) // only run once when component mounts

    // Set up an event listener for version updates outside of the initialization flow
    useEffect(() => {
        const handleVersionUpdate = event => {
            if (event.detail && typeof event.detail.isUpdateAvailable === 'boolean') {
                setIsUpdateAvailable(event.detail.isUpdateAvailable)
            }
        }

        window.addEventListener('versionCheckUpdate', handleVersionUpdate)

        return () => {
            window.removeEventListener('versionCheckUpdate', handleVersionUpdate)
        }
    }, [])

    return { isUpdateAvailable }
}

// Add TypeScript interface for window.Knov
declare global {
    interface Window {
        Knov: {
            version?: {
                buildTimeUnixTimestamp: number
                gitCommitHash: string
            }
            isUpdateAvailable?: boolean
            checkVersionUpdate?: () => Promise<unknown>
            [key: string]: unknown
        }
    }
}
